import React, {useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReactECharts from 'echarts-for-react';

const {FiEdit, FiCalendar, FiDollarSign, FiTrendingUp, FiPercent, FiChevronDown, FiChevronUp, FiList} = FiIcons;

function RetirementGoalCard({retirementPlan, userProfile, retirementMetrics, onEdit}) {
  const [showDetailedCashflow, setShowDetailedCashflow] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {style: 'currency', currency: 'INR', minimumFractionDigits: 0}).format(value);
  };

  const yearsToRetirement = userProfile.retirementAge - userProfile.currentAge;
  const retirementDuration = userProfile.lifeExpectancy - userProfile.retirementAge;

  // Same fallback used in calculateRetirementMetrics: profile's post-retirement
  // expense estimate until the user explicitly customizes the retirement plan.
  const effectiveMonthlyIncomeRequired = retirementPlan.monthlyIncomeRequired || userProfile.postRetirementMonthlyExpense || 0;
  const postRetirementReturn = retirementMetrics?.postRetirementReturn || 7;

  // Calculate what-if values
  const whatIfPercentage = retirementPlan.whatIfPercentage || 40;
  const standardCorpusRequired = retirementMetrics.corpusRequired || 0;
  const whatIfCorpusRequired = standardCorpusRequired * (1 + whatIfPercentage/100);
  const whatIfRequiredSIP = (retirementMetrics.requiredSIP || 0) * (1 + whatIfPercentage/100);
  const whatIfRequiredLumpsum = (retirementMetrics.requiredLumpsum || 0) * (1 + whatIfPercentage/100);

  const corpusGrowthOption = {
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const age = userProfile.currentAge + parseInt(params[0].axisValue);
        return `Age ${age}: ${formatCurrency(params[0].value)}`;
      }
    },
    xAxis: {
      type: 'category',
      data: Array.from({length: yearsToRetirement}, (_, i) => i + 1),
      axisLabel: {
        fontSize: 10
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 10,
        formatter: (value) => `₹${(value / 10000000).toFixed(1)}Cr`
      }
    },
    series: [{
      data: retirementMetrics?.corpusGrowth || [],
      type: 'line',
      smooth: true,
      itemStyle: {color: '#3b82f6'},
      areaStyle: {color: 'rgba(59,130,246,0.1)'}
    }],
    grid: {
      left: '10%',
      right: '5%',
      top: '10%',
      bottom: '15%'
    }
  };

  // Funding Coverage: the actual ratio of projected corpus to the corpus you'll
  // need at retirement, expressed as a capped percentage. Unlike the goal-level
  // "Affordability Score", this one genuinely is the coverage ratio — so it
  // keeps its own honest label instead of borrowing the word "probability".
  const calculateFundingCoverage = () => {
    if (!retirementMetrics || !retirementMetrics.corpusRequired) return 0;

    const totalProjectedCorpus = retirementMetrics.corpusGrowth?.[retirementMetrics.corpusGrowth.length - 1] || 0;
    const coverageRatio = totalProjectedCorpus / retirementMetrics.corpusRequired;

    return Math.round(Math.min(100, Math.max(0, coverageRatio * 100)));
  };

  const achievementProbability = calculateFundingCoverage();

  // ─── Detailed post-retirement cashflow ───────────────────────────────────
  // For each year of retirement: opening balance, growth at the post-retirement
  // return rate, the inflation-adjusted withdrawal, and the resulting closing
  // balance. Mirrors the loop in calculateRetirementMetrics exactly.
  const cashflowRows = (retirementMetrics?.cashflowYears || []).map((yearIndex, i) => {
    const openingBalance = retirementMetrics.availableCashflow[i] || 0;
    const annualWithdrawal = retirementMetrics.requiredCashflow[i] || 0;
    const growthAmount = openingBalance * (postRetirementReturn / 100);
    const isLastYear = i === retirementMetrics.cashflowYears.length - 1;
    const closingBalance = isLastYear
      ? retirementMetrics.finalCorpus || 0
      : retirementMetrics.availableCashflow[i + 1] || 0;

    return {
      year: yearIndex,
      age: userProfile.retirementAge + yearIndex - 1,
      openingBalance,
      growthAmount,
      monthlyIncome: annualWithdrawal / 12,
      annualWithdrawal,
      closingBalance
    };
  });

  const drawdownChartOption = {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const row = cashflowRows[params[0].dataIndex];
        if (!row) return '';
        return `Age ${row.age}<br/>Balance: ${formatCurrency(row.closingBalance)}<br/>Withdrawal: ${formatCurrency(row.annualWithdrawal)}/yr`;
      }
    },
    legend: {data: ['Corpus Balance', 'Annual Withdrawal'], top: 0, textStyle: {fontSize: 10}},
    xAxis: {
      type: 'category',
      data: cashflowRows.map(r => r.age),
      name: 'Age',
      nameLocation: 'middle',
      nameGap: 22,
      axisLabel: {fontSize: 10}
    },
    yAxis: {
      type: 'value',
      axisLabel: {fontSize: 10, formatter: (value) => `₹${(value / 10000000).toFixed(1)}Cr`}
    },
    series: [
      {
        name: 'Corpus Balance',
        type: 'line',
        data: cashflowRows.map(r => r.closingBalance),
        itemStyle: {color: '#3b82f6'},
        areaStyle: {color: 'rgba(59,130,246,0.1)'}
      },
      {
        name: 'Annual Withdrawal',
        type: 'line',
        data: cashflowRows.map(r => r.annualWithdrawal),
        itemStyle: {color: '#f58320'}
      }
    ],
    grid: {left: '12%', right: '5%', top: '18%', bottom: '18%'}
  };

  const getStatusColor = (probability) => {
    if (probability >= 90) return 'text-green-600 bg-green-100';
    if (probability >= 70) return 'text-[#f58320] bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const getStatusText = (probability) => {
    if (probability >= 90) return 'On Track';
    if (probability >= 70) return 'Needs Attention';
    return 'At Risk';
  };

  return (
    <motion.div
      initial={{opacity: 0, y: 20}}
      animate={{opacity: 1, y: 0}}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
    >
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-xl font-semibold text-gray-900">Retirement</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(achievementProbability)}`}>
              {getStatusText(achievementProbability)}
            </span>
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <SafeIcon icon={FiCalendar} className="text-xs" />
              <span>Age {userProfile.retirementAge} ({yearsToRetirement} years)</span>
            </div>
            <div className="flex items-center space-x-1">
              <SafeIcon icon={FiDollarSign} className="text-xs" />
              <span>{formatCurrency(effectiveMonthlyIncomeRequired)}/month (today's value)</span>
            </div>
            <div className="flex items-center space-x-1">
              <SafeIcon icon={FiTrendingUp} className="text-xs" />
              <span>{userProfile.returnExpectations.longTerm}% return</span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onEdit}
            className="p-2 text-gray-400 hover:text-blue-500 hover:bg-gray-50 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiEdit} className="text-sm" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Metrics */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-600 uppercase tracking-wide">Corpus Required</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(retirementMetrics?.corpusRequired || 0)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-600 uppercase tracking-wide">Required SIP</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(retirementMetrics?.requiredSIP || 0)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-600 uppercase tracking-wide">Required Lumpsum</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(retirementMetrics?.requiredLumpsum || 0)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-600 uppercase tracking-wide">Current SIP</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(retirementPlan.sipContribution)}/month</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
            <p className="text-xs text-gray-600 uppercase tracking-wide mb-3">Retirement Details</p>
            <div className="grid grid-cols-1 gap-3 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Years to Retirement:</span>
                <span className="font-bold text-blue-700">{yearsToRetirement} years</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Retirement Duration:</span>
                <span className="font-bold text-blue-700">{retirementDuration} years</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Monthly Income at Retirement:</span>
                <span className="font-bold text-blue-700">
                  {formatCurrency(retirementPlan.monthlyIncomeRequired * Math.pow(1 + userProfile.inflationRates.general / 100, yearsToRetirement))}
                </span>
              </div>
              <div className="flex justify-between items-center border-t border-blue-200 pt-2 mt-1">
                <span className="text-gray-600 font-medium">Funding Coverage:</span>
                <span className={`font-bold ${
                  achievementProbability >= 90 ? 'text-green-700' : 
                  achievementProbability >= 70 ? 'text-orange-700' : 
                  'text-red-700'
                }`}>
                  {achievementProbability}%
                </span>
              </div>
            </div>
          </div>

          {/* What-If Analysis */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <SafeIcon icon={FiPercent} className="text-orange-600 text-sm" />
              <p className="text-xs text-gray-600 uppercase tracking-wide">What-If Scenario (+{whatIfPercentage}%)</p>
            </div>
            <div className="grid grid-cols-1 gap-3 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">What-If Corpus Required:</span>
                <span className="font-bold text-orange-700">{formatCurrency(whatIfCorpusRequired)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">What-If SIP Required:</span>
                <span className="font-bold text-orange-700">{formatCurrency(whatIfRequiredSIP)}/month</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">What-If Lumpsum Required:</span>
                <span className="font-bold text-orange-700">{formatCurrency(whatIfRequiredLumpsum)}</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#19b48d]/10 to-[#19b48d]/5 rounded-lg p-4">
            <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Funding Coverage</p>
            <p className="text-[10px] text-gray-500 mb-2">Projected corpus at retirement as a % of the corpus you'll need. Not a probability of success.</p>
            <div className="flex items-center space-x-3">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    achievementProbability >= 90 ? 'bg-green-500' : 
                    achievementProbability >= 70 ? 'bg-[#f58320]' : 
                    'bg-red-500'
                  }`} 
                  style={{width: `${achievementProbability}%`}} 
                />
              </div>
              <span className="text-sm font-semibold text-gray-900">{achievementProbability}%</span>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-600 uppercase tracking-wide mb-3">Corpus Growth Path</p>
          <div className="h-40">
            <ReactECharts option={corpusGrowthOption} style={{height: '100%', width: '100%'}} />
          </div>

          {/* Investment Comparison */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wide mb-2">Total Retirement SIP</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatCurrency((retirementPlan.sipContribution + (retirementMetrics?.requiredSIP || 0)) * 12 * yearsToRetirement)}
                </p>
                <p className="text-xs text-gray-500">Over {yearsToRetirement} years</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wide mb-2">Final Corpus</p>
                <p className="text-sm font-medium text-blue-600">
                  {formatCurrency(retirementMetrics?.corpusGrowth?.[retirementMetrics.corpusGrowth.length - 1] || 0)}
                </p>
                <p className="text-xs text-gray-500">
                  At age {userProfile.retirementAge}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Post-Retirement Cashflow */}
      {cashflowRows.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Retirement Cashflow</h4>
              <p className="text-xs text-gray-500">
                Monthly income need vs. remaining corpus, growing at {postRetirementReturn}% p.a. through retirement
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowDetailedCashflow(!showDetailedCashflow)}
              className="flex items-center space-x-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 flex-shrink-0"
            >
              <SafeIcon icon={FiList} className="text-sm" />
              <span>{showDetailedCashflow ? 'Hide' : 'View'} year-by-year</span>
              <SafeIcon icon={showDetailedCashflow ? FiChevronUp : FiChevronDown} className="text-sm" />
            </button>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="h-48">
              <ReactECharts option={drawdownChartOption} style={{height: '100%', width: '100%'}} />
            </div>
          </div>

          <AnimatePresence>
            {showDetailedCashflow && (
              <motion.div
                initial={{opacity: 0, height: 0}}
                animate={{opacity: 1, height: 'auto'}}
                exit={{opacity: 0, height: 0}}
                className="overflow-hidden"
              >
                <div className="mt-4 overflow-x-auto border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
                  <table className="w-full text-xs min-w-[560px]">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Age</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700">Opening Balance</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700">Growth ({postRetirementReturn}%)</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700">Monthly Income</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700">Annual Withdrawal</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700">Closing Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cashflowRows.map((row) => (
                        <tr key={row.year} className="border-t border-gray-100">
                          <td className="py-2 px-3 font-medium text-gray-900">{row.age}</td>
                          <td className="py-2 px-3 text-right text-gray-700">{formatCurrency(row.openingBalance)}</td>
                          <td className="py-2 px-3 text-right text-green-600">+{formatCurrency(row.growthAmount)}</td>
                          <td className="py-2 px-3 text-right text-gray-700">{formatCurrency(row.monthlyIncome)}</td>
                          <td className="py-2 px-3 text-right text-orange-600">-{formatCurrency(row.annualWithdrawal)}</td>
                          <td className={`py-2 px-3 text-right font-medium ${row.closingBalance <= 0 ? 'text-red-600' : 'text-gray-900'}`}>
                            {formatCurrency(row.closingBalance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {cashflowRows[cashflowRows.length - 1]?.closingBalance <= 0 && (
                  <p className="text-xs text-red-600 mt-2">
                    Your corpus is projected to run out before life expectancy at current assumptions — consider a higher SIP, later retirement, or reviewing your post-retirement return assumption.
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
}

export default RetirementGoalCard;